import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { sapi, errText, PARTICIPANT_COLOR } from "@/lib/strategy";
import { useStrategy } from "@/context/StrategyContext";

export default function StrategyEntry() {
  const { login } = useStrategy();
  const [people, setPeople] = useState([]);
  const [pid, setPid] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { sapi.get("/participants").then((r) => setPeople(r.data)).catch(() => setErr("Could not load participants")); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pid) { setErr("Select your name first"); return; }
    setBusy(true); setErr("");
    try { await login(pid, code); } catch (ex) { setErr(errText(ex, "Incorrect passcode")); } finally { setBusy(false); }
  };

  return (
    <div className="st-root min-h-screen grid place-items-center px-5 py-10 relative overflow-hidden st-grain">
      <div className="blob" style={{ width: 700, height: 700, top: -300, left: -200, background: "radial-gradient(circle, rgba(200,247,74,.14), transparent 70%)" }} />
      <div className="blob" style={{ width: 500, height: 500, bottom: -200, right: -100, background: "radial-gradient(circle, rgba(111,168,255,.12), transparent 70%)" }} />
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="relative z-10 w-full max-w-xl">
        <div className="mb-10">
          <p className="st-caps mb-3">Private · Founders only</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.02]">
            <span className="st-grad-text">GROWTH</span> <span className="st-faint font-semibold">|</span> Strategy Intelligence
          </h1>
          <p className="st-muted mt-4 text-sm sm:text-base leading-relaxed">Building the Intelligence Behind Better Business. Select your name and enter the shared founders' passcode.</p>
        </div>
        <form onSubmit={submit} className="st-panel p-6 sm:p-8 space-y-6">
          <div>
            <p className="st-caps mb-3">Who are you?</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {people.map((p) => (
                <button type="button" key={p.id} onClick={() => { setPid(p.id); setErr(""); }} data-testid={`entry-participant-${p.id}`}
                  className="st-option flex-col !items-start !gap-2" data-active={pid === p.id}>
                  <span className="grid place-items-center w-9 h-9 rounded-full font-bold" style={{ background: `${PARTICIPANT_COLOR[p.id]}22`, color: PARTICIPANT_COLOR[p.id] }}>{p.name[0]}</span>
                  <span className="font-bold">{p.name}</span>
                  <span className="st-faint text-xs leading-snug">{p.role_title}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="st-caps mb-2">Founders' passcode</p>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 st-faint" />
              <input type="password" value={code} onChange={(e) => { setCode(e.target.value); setErr(""); }} placeholder="Passcode" data-testid="entry-passcode" className="st-input !pl-11" autoComplete="current-password" />
            </div>
            {err && <p className="text-sm mt-2 font-medium" style={{ color: "var(--st-red)" }} data-testid="entry-error">{err}</p>}
          </div>
          <button type="submit" disabled={busy} className="st-btn st-btn-primary w-full !py-3" data-testid="entry-submit">
            {busy ? "Checking…" : "Enter Strategy Intelligence"} <ArrowRight size={16} />
          </button>
          <p className="st-faint text-xs text-center leading-relaxed">Your answers stay private until all three founders have submitted. This tool exists to uncover differences before they become expensive — not to manufacture agreement.</p>
        </form>
      </motion.div>
    </div>
  );
}
